import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Switch } from 'react-router-dom';
import { Router } from 'react-router';
import EndUserMonitoring from '~/components/EndUserMonitoring';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import classnames from 'classnames';

import { EventBusBridge, Page, TrackingWrapper } from '@piggybank/core';
import { Wizard, WizardPage } from '@piggybank/form';

import { isMobile, isNonDiJourney } from '~/genericFunctions';
import routes from '~/data/routes';
import styles from './App.module.css';

import { pageNames } from '~/constants/pageNames';

import EnterAmount from '~/pages/EnterAmount';
import PayrollReview from '~/pages/PayrollReview';
import PayrollReviewDi from '~/pages/PayrollReviewDi';
import SuccessPage from '~/pages/SuccessPage';
import ErrorPage from '~/pages/ErrorPage';
import Loading from '~/pages/Loading';
import Timeout from '~/pages/Timeout';
import TimeoutDialog from '~/pages/TimeoutDialog';

import { createTranslator } from '~/lib/nls-helper';
import getEnvironment from '~/lib/environment';
import getSpecs from '~/lib/tealium/spec';
import { link, view } from '~/lib/tealium/actions';

import { extendSaasToken, logOut, submitLoansApplication, submitCalculationRequest } from '~/apis';

import nls from '~/nls/App';

export const AppContext = React.createContext();

const environment = getEnvironment();

const track = event => {
  if (environment.tealium.isEnabled && event) {
    const { content } = event;
    const isViewEvent = !!get(event, 'rule.isViewEvent');
    isViewEvent? view(content): link(content);
  }
};

const NLS = createTranslator(nls);

const setPageTitle = () => {
  document.title = NLS`Loan details summary`;
};

//in seconds
const SESSION_DURATION = environment?.session?.timeoutAfterMinutes * 60 || 600;
const SHOW_TIMEOUT_DIALOG_ON = environment?.session?.timeoutWarningAfterMinutes * 60 || 540;
const TIMEOUT_DURATION = SESSION_DURATION - SHOW_TIMEOUT_DIALOG_ON;

class App extends Component {
  static propTypes = {
    globalData: PropTypes.objectOf(PropTypes.string),
    history: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.object,
    ]),
    loanOfferData: PropTypes.shape({
      customerDetails: PropTypes.objectOf(
        PropTypes.object,
      ),
      keyDetails: PropTypes.objectOf(
        PropTypes.string,
      ),
      loanOffer: PropTypes.objectOf(
        PropTypes.oneOfType([
          PropTypes.array,
          PropTypes.object,
          PropTypes.string,
        ])
      ),
    }),
  };

  state = {
    currentPage: isEmpty(this.props.loanOfferData)? routes['error'].pageName: isNonDiJourney(this.props.globalData.type)? routes['payroll-review'].pageName: routes['enter-amount'].pageName,
    displayData: {
      globalData: this.props.globalData,
      loanOffer: get(this.props.loanOfferData, 'loanOffer'),
    },
    isMobile: isMobile(get(this.props.globalData, 'ismobile')),
    ui: {
      loading: false,
      submitFrom: '',
    },
    timeoutCountDown: TIMEOUT_DURATION,
  };

  componentDidMount () {
    setPageTitle();
    this.restartSessionTimer();
    if (!isEmpty(this.props.loanOfferData)) {
      if (isNonDiJourney(this.props.globalData.type)) {
        this.props.history.push(routes['payroll-review'].uri + this.props.history.location.search);
      } else {
        this.props.history.push(routes['enter-amount'].uri + this.props.history.location.search);
      }
    }
  }

  componentWillUnmount () {
    clearTimeout(this.sessionTimer);
  }

  restartSessionTimer = () => {
    if (!this.state.isMobile) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = setTimeout(this.timeOutTick, SHOW_TIMEOUT_DIALOG_ON * 1000);
      this.setState({ timeoutCountDown: TIMEOUT_DURATION });
    }
  };

  timeOutTick = () => {
    if (this.state.timeoutCountDown <= 0) {
      logOut().finally(() => {
        this.props.history.push((routes['timeout'].uri + this.props.history.location.search));
        this.setState({ currentPage: routes['timeout'].pageName });
      });
      return;
    }

    if (this.state.currentPage !== routes['error'].pageName) {
      this.setState(state => ({ timeoutCountDown: state.timeoutCountDown - 1 }));
      this.sessionTimer = setTimeout(this.timeOutTick, 1000);
    } else {
      this.setState({ timeoutCountDown: 0 });
    }
  };

  extendSession = async () => {
    try {
      await extendSaasToken({
        globalData: this.props.globalData,
      });
      this.restartSessionTimer();
    } catch (err) {
      this.props.history.push(routes['timeout'].uri + this.props.history.location.search);
      this.setState({ currentPage: routes['timeout'].pageName });
    }
  };

  endOfJourney = async () => {
    window.location.assign(environment.bankingDashboardUrl);
  };

  handleCalculation = async (values) => {
    try {
      this.setState({
        displayData: {
          ...this.state.displayData,
          requestedAmount: values.requestedAmount,
        },
        ui: {
          loading: true,
          submitFrom: pageNames.enterAmount,
        },
      });
      let response = await submitCalculationRequest({
        globalData: this.props.globalData,
        requestData: {
          formData: values,
          loanOfferData: this.props.loanOfferData,
        },
      });
      this.setState({
        displayData: {
          globalData: this.props.globalData,
          loanOffer: {
            heading: get(this.props.loanOfferData, 'loanOffer.heading'),
            ...response.loanOffer,
          },
        },
      });
      this.props.history.push(routes['payroll-review-di'].uri + this.props.history.location.search);
      this.setState({
        ui: {
          loading: false,
          submitFrom: '',
        },
      });
    } catch (err) {
      this.props.history.push(routes['error'].uri + this.props.history.location.search);
      this.setState({ currentPage: routes['error'].pageName });
      this.setState({
        ui: {
          loading: false,
          submitFrom: '',
        },
      });
    }
  };

  handleSubmit = async (values) => {
    try {
      this.setState({
        displayData: {
          ...this.state.displayData,
          loanAmountIndex: get(values, 'loanAmountIndex'),
        },
        ui: {
          loading: true,
          submitFrom: pageNames.loanReview,
        },
      });
      let response = await submitLoansApplication({
        globalData: this.props.globalData,
        requestData: {
          customerDetails: get(this.props.loanOfferData, 'customerDetails'),
          formData: values,
          keyDetailsData: get(this.props.loanOfferData, 'keyDetails'),
          loanOfferData: get(this.state.displayData, 'loanOffer'),
        },
      });
      this.setState({
        displayData: {
          ...this.state.displayData,
          folio: response.folio,
        },
      });
      (response.responseCode === 'SUCCESS')? this.props.history.push(routes['loan-success'].uri + this.props.history.location.search): this.props.history.push(routes['error'].uri + this.props.history.location.search);
      this.setState({
        ui: {
          loading: false,
          submitFrom: '',
        },
      });
    } catch (err) {
      this.props.history.push(routes['error'].uri + this.props.history.location.search);
      this.setState({
        ui: {
          loading: false,
          submitFrom: '',
        },
      });
    }
  };

  renderDiJourney (classes) {
    if (this.state.ui.loading) {
      return (
        <div className={classes}>
          <Loading show/>
        </div>
      );
    } else {
      return (
        <div className={classes}>
          <Router basename="/" history={this.props.history}>
            <Switch>
              <Route exact path={routes['enter-amount'].uri}>
                {({ history }) => (
                  <Wizard history={history} onSubmit={this.handleCalculation}>
                    <WizardPage component={EnterAmount} />
                  </Wizard>
                )}
              </Route>
              <Route exact path={routes['payroll-review-di'].uri}>
                {({ history }) => (
                  <Wizard history={history}>
                    <WizardPage>
                      <PayrollReviewDi onContinue={this.handleSubmit} />
                    </WizardPage>
                  </Wizard>
                )}
              </Route>
              <Route path={routes['loan-success'].uri}>
                {({ history }) => (
                  <Wizard history={history} onSubmit={this.endOfJourney}>
                    <WizardPage component={SuccessPage} />
                  </Wizard>
                )}
              </Route>
              <Route path={routes['timeout'].uri}>
                {({ history }) => (
                  <div className={classnames(styles.marginTop4)}>
                    <Wizard history={history} >
                      <WizardPage component={Timeout} />
                    </Wizard>
                  </div>
                )}
              </Route>
              <Route>
                <ErrorPage globalData={this.props.globalData}/>
              </Route>
            </Switch>
          </Router>
          <TimeoutDialog
            extendSession={this.extendSession}
            show={this.state.timeoutCountDown < TIMEOUT_DURATION && this.state.timeoutCountDown > 0}
            timeoutCountDown={this.state.timeoutCountDown}
          />
        </div>
      );
    }
  }

  renderLoansJourney (classes) {
    if (this.state.ui.loading) {
      return (
        <div className={classes}>
          <Loading show/>
        </div>
      );
    } else {
      return (
        <div className={classes}>
          <Router basename="/" history={this.props.history}>
            <Switch>
              <Route exact path={routes['payroll-review'].uri}>
                {({ history }) => (
                  <Wizard history={history} initialValues={this.state.displayData} onSubmit={this.handleSubmit}>
                    <WizardPage component={PayrollReview} />
                  </Wizard>
                )}
              </Route>
              <Route exact path={routes['loan-success'].uri}>
                {({ history }) => (
                  <Wizard history={history} onSubmit={this.endOfJourney}>
                    <WizardPage component={SuccessPage} />
                  </Wizard>
                )}
              </Route>
              <Route path={routes['timeout'].uri}>
                {({ history }) => (
                  <div className={classnames(styles.marginTop4)}>
                    <Wizard history={history} >
                      <WizardPage component={Timeout} />
                    </Wizard>
                  </div>
                )}
              </Route>
              <Route>
                <ErrorPage globalData={this.props.globalData}/>
              </Route>
            </Switch>
          </Router>
          <TimeoutDialog
            extendSession={this.extendSession}
            show={this.state.timeoutCountDown < TIMEOUT_DURATION && this.state.timeoutCountDown > 0}
            timeoutCountDown={this.state.timeoutCountDown}
          />
        </div>
      );
    }
  }

  renderDesktopJourney (classes) {
    return (
      <Page>
        { isNonDiJourney(this.props.globalData.type)?
          this.renderLoansJourney(classes):
          this.renderDiJourney(classes)
        }
      </Page>
    );
  }

  renderMobileJourney (classes) {
    const Empty = () => (
      <div />
    );
    return (
      <Page footerSlot={<Empty />} headerSlot={<Empty />}>
        { isNonDiJourney(this.props.globalData.type)?
          this.renderLoansJourney(classes):
          this.renderDiJourney(classes)
        }
      </Page>
    );
  }

  render () {
    return (
      <>
        <EndUserMonitoring
          appDynamicsApplicationKey={get(environment, 'appDynamics.applicationKey')}
          enabled={get(environment, 'appDynamics.isEnabled')}
        />
        <TrackingWrapper
          onTrackingEvent={events => events.map(e => track(e))}
          specs={
            getSpecs(this.state)
          }
        >
          <EventBusBridge>
            <AppContext.Provider value={this.state}>
              {isMobile(get(this.props.globalData, 'ismobile'))?
                this.renderMobileJourney(classnames(styles.mobile)):
                this.renderDesktopJourney(classnames(null))}
            </AppContext.Provider>
          </EventBusBridge>
        </TrackingWrapper>
      </>
    );
  }
}

export default App;
